/**
 * Raw payment events emitted across RBC checkout flows
 * Used by flights, hotels, cars, packages, activities
 */

import type { ProductType } from "@b2bportal/core-types";

export enum PaymentRawEventName {
  ViewPayment = "ViewPayment",
  ChoosePayment = "ChoosePayment",
}

export enum PaymentEventName {
  view_payment = "view_payment",
  choose_payment = "choose_payment",
}

export interface ViewPaymentProperties {
  product_type: ProductType | undefined;
  /** Whether the base fare exceeds the maximum allowed cap threshold (different from additional_points_purchase) */
  over_max_ticket_price?: boolean;
  /** Dollar amount user pays for fare overage (e.g., base fare $450 - max $350 = $100 overage). NOT points. */
  overage_amount?: number;
  /** Whether user needs to purchase additional points to meet fixed points cost */
  additional_points_purchase?: boolean;
  /** Total cost (in currency) of purchasing additional points */
  additional_points_purchase_total?: number;
  /** Number of additional points being purchased */
  additional_points_needed?: number;
}

/**
 * Emitted when user enters payment step (cardPayment or awardsPayment state)
 * Fires on every transition to payment (not just once)
 */
export interface ViewPaymentRawEvent {
  name: PaymentRawEventName.ViewPayment;
  properties: {
    product_type: ProductType | undefined;
    /** Whether the base fare exceeds the maximum allowed cap threshold (different from additional_points_purchase) */
    over_max_ticket_price?: boolean;
    /** Dollar amount user pays for fare overage (e.g., base fare $450 - max $350 = $100 overage). NOT points. */
    overage_amount?: number;
    /** Whether user needs to purchase additional points to meet fixed points cost */
    additional_points_purchase?: boolean;
    /** Total cost (in currency) of purchasing additional points */
    additional_points_purchase_total?: number;
    /** Number of additional points being purchased */
    additional_points_needed?: number;
  };
}

/**
 * Emitted when user selects a credit card for payment
 */
export interface ChoosePaymentRawEvent {
  name: PaymentRawEventName.ChoosePayment;
  properties: Record<string, never>;
}

export interface ViewPaymentEvent {
  name: PaymentEventName.view_payment;
  properties: {
    product_type: ProductType | undefined;
    /** Whether the base fare exceeds the maximum allowed cap threshold (different from additional_points_purchase) */
    over_max_ticket_price?: boolean;
    /** Dollar amount user pays for fare overage (e.g., base fare $450 - max $350 = $100 overage). NOT points. */
    overage_amount?: number;
    /** Whether user needs to purchase additional points to meet fixed points cost */
    additional_points_purchase?: boolean;
    /** Total cost (in currency) of purchasing additional points */
    additional_points_purchase_total?: number;
    /** Number of additional points being purchased */
    additional_points_needed?: number;
  };
}

export interface ChoosePaymentEvent {
  name: PaymentEventName.choose_payment;
  properties: Record<string, never>;
}

export type PaymentRawEvent = ViewPaymentRawEvent | ChoosePaymentRawEvent;

export type PaymentEvent = ViewPaymentEvent | ChoosePaymentEvent;
